import React, { useEffect, useRef, useState } from "react";
import { Grid, IconButton, FormControlLabel, Checkbox } from "@mui/material";
import axios from "axios";
import { useParams } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import { useStyles } from "./style";
import { ttfAction } from "../../create-loan/ttf-review/constants";
import { CircularProgressWithLabel } from "../../create-loan/ttf-review/document-review/CircularProgressWithLabel";
import { TTFReviewCacheProvider } from "../../browser-cache/local-forage/TTFReviewCacheProvider";
import {
  getPresignedURL,
  getPresignedURLForDrawDocs
} from "../../network/apiService";
import { BootstrapTooltip, InfoOnlyTooltip } from "../Tooltip";
import {
  FirstPageIcon,
  LastPageIcon,
  NextPageIcon,
  PrevPageIcon,
  RefreshIcon,
  ZoomInIcon,
  ZoomOutIcon,
  DownloadIcon,
  RedirectIcon,
  RotateClockwiseIcon
} from "../../create-loan/Icons";
import { URLParamsType } from "../constants";
import { getConfig } from "../../config/config";
import { findMinMax } from "../../create-loan/ttf-review/ttfHelperFunctions";
import { configurePdfJsWorker } from "./pdfjsWorker";

const updateControlValues = (
  payload: any,
  documentReviewDispatch: any,
  state: any
) => {
  documentReviewDispatch({
    type: ttfAction.UPDATE_PDF_CONTROLS,
    payload: {
      value: payload,
      tabToUpdate: state.activeTab
    }
  });
};

interface PDFViewerProps {
  state?: any;
  documentReviewDispatch: any;
  cacheKey?: any;
  useCache?: boolean;
  url?: string;
  name: string;
  highlightChecked?: boolean;
  highlightDisabled?: boolean;
  onChangeHighlightSelect?: Function;
  drawId?: string;
  overRideDrawIdCheck?: boolean;
}

export const PDFViewer = (props: PDFViewerProps) => {
  useEffect(() => {
    configurePdfJsWorker(pdfjsLib);
  }, []);

  const cacheProvider = TTFReviewCacheProvider();
  const pixelPadding = 7;
  const [PDFData, setPDFData] = useState<null | string>(null);
  const [currentAngle, setCurrentAngle] = useState<number>(0);
  const [downloadProgress, setDownloadProgress] = useState<any>(null);
  const [currentRequest, setCurrentRequest] = useState<any>(null);
  const [redownload, setRedownload] = useState<boolean>(false);
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [zoom, setZoom] = useState<number>(1);
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [viewerWidth, setViewerWidth] = useState<number>(0);
  const viewerScrollRef = useRef<HTMLDivElement | null>(null);
  const pageCanvasRefs = useRef<{ [page: number]: HTMLCanvasElement | null }>({});
  const pageContainerRefs = useRef<{ [page: number]: HTMLDivElement | null }>({});
  const renderTaskRef = useRef<any>(null);
  const shouldAutoScrollToPageRef = useRef<boolean>(false);
  const classes = useStyles();
  const {
    state,
    documentReviewDispatch,
    cacheKey,
    url,
    useCache = true,
    name,
    highlightChecked,
    highlightDisabled,
    drawId: drawIdFromProps,
    overRideDrawIdCheck = false,
    onChangeHighlightSelect
  } = props;
  const { loanId, loanType, loanStage, drawId } = useParams<URLParamsType>();
  const effectiveDrawId = drawIdFromProps || drawId;
  const activeTabState = state ? state[state.activeTab] : {};
  const { activePageNumber, highlightCoordinates = [] } = activeTabState || {};
  const setPageFromControl = (pageNo: number) => {
    shouldAutoScrollToPageRef.current = true;
    setCurrentPage(pageNo);
  };

  const onClickOpenInNewTab = () => {
    window.open(
      `${
        getConfig().redirectUrl
      }internal/loan/createLoan/${loanType}/${loanStage}/${loanId}/documentViewer/${cacheKey}/${name}`
    );
  };

  const onClickDownload = (downloadUrl: string) => {
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.setAttribute("download", name); // any other extension
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleRotate = () => {
    setCurrentAngle((currentAngle: number) => (currentAngle + 90) % 360);
  };

  const downloadFromServer = async () => {
    const ourRequest = axios.CancelToken.source();
    setCurrentRequest(ourRequest);
    let downloadURL = url;
    if (!downloadURL && cacheKey) {
      if (effectiveDrawId && !overRideDrawIdCheck) {
        const res: any = await getPresignedURLForDrawDocs(
          effectiveDrawId,
          Number(cacheKey)
        );
        downloadURL = res?.data?.downloadLinks?.[0].url;
      } else if (loanId) {
        const responseData: any = await getPresignedURL(
          "LOAN",
          loanId,
          cacheKey
        );
        downloadURL = responseData?.data?.url;
      }
    }
    if (!downloadURL) return null;

    let response;
    response = await axios({
      url: downloadURL,
      cancelToken: ourRequest.token,
      responseType: "blob",
      onDownloadProgress(progressEvent: any) {
        const progress = Math.round(
          (progressEvent?.loaded / progressEvent?.total) * 100
        );
        setDownloadProgress(progress);
      }
    });
    const { data } = response;
    if (cacheKey) {
      cacheProvider.setItem(cacheKey, data);
    }
    return data;
  };

  const readFile = async (useCache = false) => {
    try {
      const data =
        (useCache && cacheKey && (await cacheProvider.getItem(cacheKey))) ||
        (await downloadFromServer());
      if (url) {
        setPDFData(url);
      } else {
        setPDFData(globalThis.URL.createObjectURL(data));
      }
    } catch {
      console.info(`Error reading data from cache`);
    }
  };

  const triggerRender = () => {
    if (currentRequest !== null) {
      currentRequest.cancel();
      setCurrentRequest(null);
    }
    setPDFData(null);
    const payload = {
      isDocumentLoading: true
    };
    if (state) {
      updateControlValues(payload, documentReviewDispatch, state);
    }
    setDownloadProgress(null);
    readFile(useCache);
  };

  useEffect(() => {
    setCurrentAngle(0);
  }, [cacheKey]);

  useEffect(() => {
    triggerRender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, redownload, url]);

  useEffect(() => {
    if (!PDFData) return;
    let cancelled = false;
    const loadPdf = async () => {
      const task = pdfjsLib.getDocument(PDFData);
      const doc = await task.promise;
      if (cancelled) return;
      setPdfDoc(doc);
      setTotalPages(doc.numPages || 0);
      const nextPage =
        highlightCoordinates.length && state
          ? findMinMax(highlightCoordinates, "pageNo").min
          : 1;
      shouldAutoScrollToPageRef.current = true;
      setCurrentPage(nextPage);
      if (state) {
        updateControlValues(
          {
            totalPages: doc.numPages,
            activePageNumber: nextPage,
            isDocumentLoading: false
          },
          documentReviewDispatch,
          state
        );
      }
    };
    loadPdf().catch(() => {
      setPdfDoc(null);
      setTotalPages(0);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PDFData]);

  useEffect(() => {
    if (!state || !activePageNumber) return;
    shouldAutoScrollToPageRef.current = true;
    setCurrentPage(activePageNumber);
  }, [activePageNumber, state]);

  useEffect(() => {
    const viewer = viewerScrollRef.current;
    if (!viewer) return;

    const updateViewerWidth = () => {
      setViewerWidth(viewer.clientWidth || 0);
    };

    updateViewerWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateViewerWidth);
      return () => {
        window.removeEventListener("resize", updateViewerWidth);
      };
    }

    const observer = new ResizeObserver(() => {
      updateViewerWidth();
    });
    observer.observe(viewer);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;
    const renderAllPages = async () => {
      setLoadingPage(true);
      for (let pageNo = 1; pageNo <= totalPages; pageNo += 1) {
        if (cancelled) break;
        const canvas = pageCanvasRefs.current[pageNo];
        if (!canvas) continue;
        const page = await pdfDoc.getPage(pageNo);
        const baseRotation = Number(page.rotate || 0);
        const effectiveRotation = (baseRotation + currentAngle) % 360;
        const baseViewport = page.getViewport({
          scale: 1,
          rotation: effectiveRotation
        });
        // Read current pane width at render-time so 100% truly maps to page-width fit.
        const liveViewerWidth =
          viewerScrollRef.current?.clientWidth || viewerWidth || 0;
        const hasUsableViewerWidth = liveViewerWidth > 50;
        const availableViewerWidth = hasUsableViewerWidth
          ? Math.max(liveViewerWidth - 12, 1)
          : 0;
        const fitScale = hasUsableViewerWidth
          ? availableViewerWidth / Math.max(baseViewport.width, 1)
          : 1;
        const effectiveScale = zoom * fitScale;
        // Render at device pixel ratio so text stays sharp on HiDPI (react-pdf-viewer did this for you).
        const rawPixelRatio = typeof window !== "undefined" ? window.devicePixelRatio : 1;
        const pixelRatio = Math.max(1, Math.min(rawPixelRatio || 1, 3));
        const viewport = page.getViewport({
          scale: effectiveScale * pixelRatio,
          rotation: effectiveRotation
        });
        const context = canvas.getContext("2d");
        if (!context) continue;
        context.setTransform(1, 0, 0, 1, 0, 0);
        const cssWidth = viewport.width / pixelRatio;
        const cssHeight = viewport.height / pixelRatio;
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${cssWidth}px`;
        canvas.style.height = `${cssHeight}px`;
        renderTaskRef.current = page.render({
          canvasContext: context,
          viewport
        });
        await renderTaskRef.current.promise;
      }
      if (!cancelled) setLoadingPage(false);
    };
    renderAllPages().catch(() => setLoadingPage(false));
    return () => {
      cancelled = true;
      if (renderTaskRef.current?.cancel) {
        renderTaskRef.current.cancel();
      }
    };
  }, [pdfDoc, totalPages, zoom, currentAngle, viewerWidth]);

  useEffect(() => {
    if (!currentPage) return;
    if (!shouldAutoScrollToPageRef.current) return;
    const viewer = viewerScrollRef.current;
    const container = pageContainerRefs.current[currentPage];
    if (container && viewer) {
      // Keep navigation scroll scoped to PDF pane only; do not scroll outer page.
      viewer.scrollTo({
        left: 0,
        top: Math.max(0, container.offsetTop - 8),
        behavior: "auto"
      });
    }
    shouldAutoScrollToPageRef.current = false;
  }, [currentPage]);

  const onDocumentLoadSuccess = () => {
    const payload = {
      totalPages,
      activePageNumber: highlightCoordinates.length
        ? findMinMax(highlightCoordinates, "pageNo").min
        : 1,
      isDocumentLoading: false
    };
    if (state) {
      updateControlValues(payload, documentReviewDispatch, state);
    }
  };

  const getBoundingRectCoordinates = (
    item: any,
    canvasWidth: number,
    canvasHeight: number,
    effectiveRotation: number
  ) => {
    switch (effectiveRotation) {
      case 0: {
        const left = item.topLeft?.[0] * canvasWidth;
        const top = item.topLeft?.[1] * canvasHeight;
        const width = (item.bottomRight?.[0] - item.topLeft?.[0]) * canvasWidth;
        const height =
          (item.bottomRight?.[1] - item.topLeft?.[1]) * canvasHeight;
        return { left, top, width, height };
      }
      case 90: {
        const left = canvasWidth - item.bottomRight?.[1] * canvasWidth;
        const top = item.topLeft?.[0] * canvasHeight;
        const width = (item.bottomRight?.[1] - item.topLeft?.[1]) * canvasWidth;
        const height =
          (item.bottomRight?.[0] - item.topLeft?.[0]) * canvasHeight;
        return { left, top, width, height };
      }
      case 180: {
        const left = canvasWidth - item.bottomRight?.[0] * canvasWidth;
        const top = canvasHeight - item.bottomRight?.[1] * canvasHeight;
        const width = (item.bottomRight?.[0] - item.topLeft?.[0]) * canvasWidth;
        const height =
          (item.bottomRight?.[1] - item.topLeft?.[1]) * canvasHeight;
        return { left, top, width, height };
      }
      case 270: {
        const left = item.topLeft?.[1] * canvasWidth;
        const top = canvasHeight - item.bottomRight?.[0] * canvasHeight;
        const width = (item.bottomRight?.[1] - item.topLeft?.[1]) * canvasWidth;
        const height =
          (item.bottomRight?.[0] - item.topLeft?.[0]) * canvasHeight;
        return { left, top, width, height };
      }
      default:
        break;
    }
    const left = item.topLeft?.[0] * canvasWidth;
    const top = item.topLeft?.[1] * canvasHeight;
    const width = (item.bottomRight?.[0] - item.topLeft?.[0]) * canvasWidth;
    const height = (item.bottomRight?.[1] - item.topLeft?.[1]) * canvasHeight;
    return { left, top, width, height };
  };
  useEffect(() => {
    onDocumentLoadSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  const reDownloadDoc = () => {
    setRedownload((prevState) => !prevState);
  };

  if (!PDFData) {
    return (
      <Grid className={classes.pdfContainer} style={{ alignItems: "center" }}>
        <InfoOnlyTooltip arrow placement="top" title="Re-download">
          <IconButton
            aria-label="Re-download"
            component="button"
            onClick={reDownloadDoc}
            style={{ position: "absolute", right: 0, zIndex: 1 }}
            size="large"
          >
            <RefreshIcon color="#808080" width={16} height={16} />
          </IconButton>
        </InfoOnlyTooltip>
        <CircularProgressWithLabel value={downloadProgress} />
      </Grid>
    );
  }
  const isFirstPage = currentPage <= 1;
  const isLastPage = totalPages === 0 || currentPage >= totalPages;
  const showHighlights = (highlightCoordinates?.length || 0) > 0;
  const handleViewerScroll = () => {
    const viewer = viewerScrollRef.current;
    if (!viewer || totalPages <= 1) return;
    const scrollTop = viewer.scrollTop;
    let visiblePage = 1;
    for (let pageNo = 1; pageNo <= totalPages; pageNo += 1) {
      const pageEl = pageContainerRefs.current[pageNo];
      if (!pageEl) continue;
      if (pageEl.offsetTop - 12 <= scrollTop) {
        visiblePage = pageNo;
      } else {
        break;
      }
    }
    if (visiblePage !== currentPage) {
      // Scrolling should only update indicator, not trigger scrollIntoView again.
      shouldAutoScrollToPageRef.current = false;
      setCurrentPage(visiblePage);
    }
  };

  return (
    <Grid className={classes.pdfContainer} id="pdfContainer" data-testid="pdfContainer">
      <div className={classes.controlsBox}>
        <div style={{ padding: "0px 2px" }}>
          <BootstrapTooltip title={isFirstPage ? "" : "First page"} placement="bottom" arrow>
            <span>
              <IconButton
                aria-label="first-page"
                data-testid="first-page"
                onClick={() => setPageFromControl(1)}
                className={classes.toolbarIcon}
                disabled={isFirstPage}
                size="large"
              >
                <FirstPageIcon size="18" />
              </IconButton>
            </span>
          </BootstrapTooltip>
        </div>
        <div style={{ padding: "0px 2px" }}>
          <BootstrapTooltip title={isFirstPage ? "" : "Previous page"} placement="bottom" arrow>
            <span>
              <IconButton
                aria-label="previous-page"
                data-testid="previous-page"
                onClick={() => setPageFromControl(Math.max(1, currentPage - 1))}
                className={classes.toolbarIcon}
                disabled={isFirstPage}
                size="large"
              >
                <PrevPageIcon size="18" />
              </IconButton>
            </span>
          </BootstrapTooltip>
        </div>
        <div className={classes.pageInput}>
          <input
            value={currentPage}
            onChange={(e) => {
              const val = Number(e.target.value || 1);
              if (Number.isNaN(val)) return;
              setPageFromControl(Math.max(1, Math.min(totalPages || 1, val)));
            }}
            style={{ width: 48, marginRight: 8 }}
          />
          <span style={{ marginRight: 4 }}>/</span>
          <span>{totalPages || 0}</span>
        </div>
        <div style={{ padding: "0px 2px" }}>
          <BootstrapTooltip title={isLastPage ? "" : "Next page"} placement="bottom" arrow>
            <span>
              <IconButton
                aria-label="next-page"
                data-testid="next-page"
                onClick={() => setPageFromControl(Math.min(totalPages || 1, currentPage + 1))}
                className={classes.toolbarIcon}
                disabled={isLastPage}
                size="large"
              >
                <NextPageIcon size="18" />
              </IconButton>
            </span>
          </BootstrapTooltip>
        </div>
        <div style={{ padding: "0px 2px" }}>
          <BootstrapTooltip title={isLastPage ? "" : "Last page"} placement="bottom" arrow>
            <span>
              <IconButton
                aria-label="last-page"
                data-testid="last-page"
                onClick={() => setPageFromControl(totalPages || 1)}
                className={classes.toolbarIcon}
                disabled={isLastPage}
                size="large"
              >
                <LastPageIcon size="18" />
              </IconButton>
            </span>
          </BootstrapTooltip>
        </div>

        <div style={{ padding: "0px 2px" }}>
          <BootstrapTooltip title={zoom <= 0.5 ? "" : "Zoom out"} placement="bottom" arrow>
            <span>
              <IconButton
                aria-label="zoom-out"
                data-testid="zoom-out"
                onClick={() => setZoom((prev) => Math.max(0.5, Number((prev - 0.1).toFixed(2))))}
                className={`${classes.toolbarIcon} ${classes.zoomOut}`}
                disabled={zoom <= 0.5}
                size="large"
              >
                <ZoomOutIcon size="18" />
              </IconButton>
            </span>
          </BootstrapTooltip>
        </div>
        <div style={{ padding: "0px 2px", minWidth: 50, textAlign: "center" }}>
          {(zoom * 100).toFixed(0)}%
        </div>
        <div style={{ padding: "0px 2px" }}>
          <BootstrapTooltip title={zoom >= 3 ? "" : "Zoom in"} placement="bottom" arrow>
            <span>
              <IconButton
                aria-label="zoom-in"
                data-testid="zoom-in"
                onClick={() => setZoom((prev) => Math.min(3, Number((prev + 0.1).toFixed(2))))}
                className={classes.toolbarIcon}
                disabled={zoom >= 3}
                size="large"
              >
                <ZoomInIcon size="18" />
              </IconButton>
            </span>
          </BootstrapTooltip>
        </div>
        <div style={{ padding: "0px 2px", paddingTop: 2 }}>
          <BootstrapTooltip title="Rotate clockwise" placement="bottom" arrow>
            <IconButton
              aria-label="rotate-clockwise"
              data-testid="rotate-clockwise"
              onClick={handleRotate}
              className={classes.toolbarIcon}
              size="large"
            >
              <RotateClockwiseIcon viewBox="0 3 16 16" />
            </IconButton>
          </BootstrapTooltip>
        </div>
        {highlightChecked != null && (
          <div style={{ padding: "0px 8px", position: "relative", top: -2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={highlightChecked}
                  onChange={onChangeHighlightSelect as any}
                  disabled={highlightDisabled}
                />
              }
              label="Highlight"
              className={classes.highlightCheckbox}
            />
          </div>
        )}
        {useCache && (
          <div style={{ padding: "0px 2px" }}>
            <BootstrapTooltip title="Open in new tab" placement="bottom" arrow>
              <IconButton
                aria-label="open-in-new-tab"
                data-testid="open-in-new-tab"
                onClick={onClickOpenInNewTab}
                className={classes.toolbarIcon}
                size="large"
              >
                <RedirectIcon color="#183238" width={20} height={20} />
              </IconButton>
            </BootstrapTooltip>
          </div>
        )}
        <div style={{ padding: "0px 2px", marginRight: 5 }}>
          <BootstrapTooltip title="Download" placement="bottom" arrow>
            <IconButton
              aria-label="download"
              data-testid="download"
              onClick={() => onClickDownload(PDFData || "")}
              className={classes.toolbarIcon}
              size="large"
            >
              <DownloadIcon size="21" viewBox="0 1 16 16" />
            </IconButton>
          </BootstrapTooltip>
        </div>
      </div>

      <div
        ref={viewerScrollRef}
        style={{
          position: "relative",
          overflow: "auto",
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          paddingTop: 8
        }}
        onScroll={handleViewerScroll}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            minWidth: "max-content",
            width: "max-content",
            alignItems: "flex-start"
          }}
        >
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNo) => {
            const canvas = pageCanvasRefs.current[pageNo];
            // Match CSS layout size (logical px), not backing-store size, for highlight overlays.
            const canvasWidth = canvas?.clientWidth || 0;
            const canvasHeight = canvas?.clientHeight || 0;
            const highlightsForPage = showHighlights
              ? highlightCoordinates.filter(
                  (item: any) => Number(item?.pageNo) === Number(pageNo)
                )
              : [];
            return (
              <div
                key={`pdf-page-${pageNo}`}
                ref={(el) => {
                  pageContainerRefs.current[pageNo] = el;
                }}
                className={classes.pageOutline}
                style={{
                  borderBottom: "1px solid rgb(160 160 253 / 41%)",
                  position: "relative"
                }}
              >
                <canvas
                  ref={(el) => {
                    pageCanvasRefs.current[pageNo] = el;
                  }}
                />
                {highlightsForPage.map((item: any, idx: number) => {
                  const { left, top, width, height }: any = getBoundingRectCoordinates(
                    item,
                    canvasWidth,
                    canvasHeight,
                    currentAngle
                  );
                  return (
                    <span
                      key={`hl-${pageNo}-${idx}`}
                      style={{
                        position: "absolute",
                        left: left - pixelPadding,
                        width: width + 2 * pixelPadding,
                        height: height + 2 * pixelPadding,
                        top: top - pixelPadding,
                        border: "2px solid red"
                      }}
                    />
                  );
                })}
              </div>
            );
          })}
          {loadingPage && (
            <div style={{ textAlign: "center", color: "#666" }}>Loading page...</div>
          )}
        </div>
      </div>
    </Grid>
  );
};